"use client";

import React, { useState, useMemo, useCallback } from "react";

import SearchInput from "@stadium/shared/src/components/plasmic/custom-options/category-selector/search-input";
import EmptyState from "@stadium/shared/src/components/plasmic/custom-options/category-selector/empty-state";

export interface CustomCategorySelectorProps {
  value?: string | string[];
  updateValue?: (value: string | string[]) => void;
  contextData?: {
    slugs?: Array<{ value: string; label: string }>;
    renderedSlugs?: Array<{ value: string; label: string }>;
  };
  SideModal?: React.ComponentType<{
    show: boolean;
    onClose: () => void;
    children: React.ReactNode;
  }>;
  useRenderedSlugs?: boolean;
}

// Common styles
const commonStyles = {
  container: {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
  } as React.CSSProperties,
  button: {
    width: "100%",
    height: "33px",
    background: "#f3f3f2",
    padding: "4px 8px",
    borderRadius: "5px",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    overflow: "auto",
    gap: "4px",
    scrollbarWidth: "none",
  } as React.CSSProperties,
  modalContent: {
    padding: "12px",
    fontFamily: "sans-serif",
  } as React.CSSProperties,
  categoryList: {
    maxHeight: "400px",
    overflowY: "auto" as const,
    border: "1px solid #e0e0e0",
    borderRadius: "4px",
    padding: "8px",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  } as React.CSSProperties,
  noteBox: {
    marginTop: "12px",
    padding: "8px",
    backgroundColor: "#f0f0f0",
    borderRadius: "4px",
    fontSize: "11px",
    color: "#666",
  } as React.CSSProperties,
  selectedCardsContainer: {
    display: "flex",
    flexWrap: "wrap" as const,
    gap: "4px",
    marginBottom: "16px",
    padding: "8px",
    border: "1px solid #e0e0e0",
    borderRadius: "4px",
    backgroundColor: "#fafafa",
    minHeight: "50px",
  } as React.CSSProperties,
  selectedCard: {
    display: "flex",
    alignItems: "center",
    padding: "4px 8px",
    backgroundColor: "#fff",
    borderRadius: "4px",
    fontSize: "12px",
    color: "#333",
    gap: "4px",
  } as React.CSSProperties,
  removeButton: {
    background: "none",
    border: "none",
    color: "#90908c",
    cursor: "pointer",
    fontSize: "16px",
    padding: "0",
    width: "16px",
    height: "16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "50%",
    transition: "all 0.2s",
  } as React.CSSProperties,
  emptySelectedState: {
    padding: "12px",
    textAlign: "center" as const,
    color: "#999",
    fontSize: "12px",
    fontStyle: "italic",
  } as React.CSSProperties,
};

// Selected Category Card Component
const SelectedCategoryCard: React.FC<{
  category: { value: string; label: string };
  onRemove: (value: string) => void;
}> = ({ category, onRemove }) => (
  <span style={commonStyles.selectedCard}>
    <span style={{ userSelect: "none", whiteSpace: "nowrap" }}>{category.label}</span>
    <button
      onClick={(e) => {
        e.stopPropagation();
        onRemove(category.value);
      }}
      style={commonStyles.removeButton}
      title="Remove category"
      onMouseEnter={(e) => {
        e.currentTarget.style.color = "#000";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.color = "#90908c";
      }}
    >
      ×
    </button>
  </span>
);

// Category Item Component for Multi Selection
const MultiSelectCategoryItem: React.FC<{
  category: { value: string; label: string };
  isSelected: boolean;
  onToggle: (value: string) => void;
}> = ({ category, isSelected, onToggle }) => (
  <button
    style={{
      width: "100%",
      display: "flex",
      alignItems: "center",
      padding: "6px",
      marginBottom: "4px",
      cursor: "pointer",
      borderRadius: "4px",
      backgroundColor: "#f8f8f8",
      border: "none",
    }}
    onClick={(e) => {
      e.stopPropagation();
      onToggle(category.value);
    }}
    onMouseEnter={(e) => {
      if (!isSelected) {
        e.currentTarget.style.backgroundColor = "#f0f0f0";
      }
    }}
    onMouseLeave={(e) => {
      if (!isSelected) {
        e.currentTarget.style.backgroundColor = "#f8f8f8";
      }
    }}
  >
    <span
      style={{
        fontSize: "13px",
        color: "#333",
        userSelect: "none",
      }}
    >
      {category.label}
    </span>
  </button>
);

const CustomCategorySelector: React.FC<CustomCategorySelectorProps> = (props) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const { value, updateValue, contextData, SideModal, useRenderedSlugs } = props;

  // Wrap availableCategories in useMemo to fix dependency warnings
  const availableCategories = useMemo(() => {
    const slugs = useRenderedSlugs ? contextData?.renderedSlugs : contextData?.slugs;
    return slugs ?? [];
  }, [contextData?.renderedSlugs, contextData?.slugs, useRenderedSlugs]);

  // Determine selection mode: explicit prop, or infer from value type
  const isValueArray = Array.isArray(value);

  // Multi-selection mode values (calculated for both modes but only used conditionally)
  const selectedCategories = useMemo(() => {
    const valueArray = isValueArray ? value : [];
    return new Set(valueArray || []);
  }, [value, isValueArray]);

  // Get selected category objects for display
  const selectedCategoryObjects = useMemo(() => {
    const valueArray = isValueArray ? value : [];
    return valueArray
      .map((val) => availableCategories.find((category) => category.value === val))
      .filter((category): category is { value: string; label: string } => category !== undefined);
  }, [value, isValueArray, availableCategories]);

  // Filter out selected categories from available list
  const availableCategoriesFiltered = useMemo(() => {
    if (!searchQuery.trim()) {
      return availableCategories.filter((category) => !selectedCategories.has(category.value));
    }
    const query = searchQuery.toLowerCase().trim();
    return availableCategories.filter(
      (category) =>
        !selectedCategories.has(category.value) && category.label.toLowerCase().includes(query),
    );
  }, [availableCategories, selectedCategories, searchQuery]);

  // Common handlers
  const handleOpenModal = useCallback(() => {
    setSearchQuery("");
    setOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setOpen(false);
    setSearchQuery("");
  }, []);

  // Multi-selection handlers
  const handleToggleCategory = useCallback(
    (categoryValue: string) => {
      const newSelected = new Set(selectedCategories);
      if (newSelected.has(categoryValue)) {
        newSelected.delete(categoryValue);
      } else {
        newSelected.add(categoryValue);
      }
      updateValue?.(Array.from(newSelected));
    },
    [selectedCategories, updateValue],
  );

  const handleRemoveCategory = useCallback(
    (categoryValue: string) => {
      const newSelected = new Set(selectedCategories);
      newSelected.delete(categoryValue);
      updateValue?.(Array.from(newSelected));
    },
    [selectedCategories, updateValue],
  );

  const selectedCount = selectedCategories.size;

  const renderSelectedCategories = () => {
    return selectedCategoryObjects.map((category, index) => (
      <SelectedCategoryCard
        key={`${category.value}-${index}`}
        category={category}
        onRemove={handleRemoveCategory}
      />
    ));
  };

  // Fallback if SideModal is not provided
  if (!SideModal) {
    return (
      <div style={{ padding: "10px", color: "#999", fontSize: "12px" }}>
        SideModal not available
      </div>
    );
  }

  return (
    <div style={commonStyles.container}>
      <button onClick={handleOpenModal} style={commonStyles.button}>
        {selectedCount > 0 ? renderSelectedCategories() : "Edit"}
      </button>
      <SideModal show={open} onClose={handleCloseModal}>
        <div style={commonStyles.modalContent}>
          {/* Selected Categories Cards */}
          {selectedCategoryObjects.length > 0 && (
            <div style={{ marginBottom: "16px" }}>
              <div
                style={{
                  fontSize: "13px",
                  fontWeight: "600",
                  marginBottom: "8px",
                  color: "#333",
                }}
              >
                Selected Categories ({selectedCategoryObjects.length})
              </div>
              <div style={commonStyles.selectedCardsContainer}>{renderSelectedCategories()}</div>
            </div>
          )}

          {/* Available Categories Section */}
          <div
            style={{
              fontSize: "13px",
              fontWeight: "600",
              marginBottom: "8px",
              color: "#333",
            }}
          >
            {selectedCategoryObjects.length > 0 ? "Available Categories" : "Select Categories"}
          </div>
          <SearchInput value={searchQuery} onChange={setSearchQuery} />
          <div style={commonStyles.categoryList}>
            {availableCategoriesFiltered.length === 0 ? (
              <EmptyState
                hasCategories={(availableCategories?.length ?? 0) > 0}
                searchQuery={searchQuery}
              />
            ) : (
              availableCategoriesFiltered.map((category, index) => (
                <MultiSelectCategoryItem
                  key={`${category.value}-${index}`}
                  category={category}
                  isSelected={selectedCategories?.has(category.value) || false}
                  onToggle={handleToggleCategory}
                />
              ))
            )}
          </div>
          <div style={commonStyles.noteBox}>
            <strong>Note:</strong> Selected categories will be hidden from the catalog entries
            display.
          </div>
        </div>
      </SideModal>
    </div>
  );
};

export default CustomCategorySelector;
